import { join } from 'path';
import queryString from 'query-string';
import { Location, matchPath, PathMatch, useMatches } from 'react-router-dom';

import { Logger } from '../logger/logger';
import { JSONType, Params } from './types';

/**
 * Normalise the matched portion of the URL from "react-router-v4 match object" with the passed paths.
 * Useful for building nested <Link>s
 * @param {match} matchProp - a match object contains information about how a <Route path> matched the URL.
 * @param {string[]} paths - list of path to be joined
 * @return {string} joined & normalised route url
 *
 * @example
 * const organisationID = 'xxxx-xxxx-xxxx-xxxx'
 * <Link to={joinRouteMatchUrl(match, 'organisations', organisationID)}
 */
export const joinRouteMatchUrl = (url: string, ...paths: string[]): string => {
  // url - (string) The matched portion of the URL. Useful for building nested <Link>s

  return join(url, ...paths);
};

/**
 * Normalise the path pattern used to match from "react-router-v4 match object" with the passed paths.
 * Useful for building nested <Route>s
 * @param matchProp - the object returned by useMatch(), or null
 * @param paths - additional paths to append
 * @returns joined path string
 *
 * @example
 * <Route path={joinRouteMatchPath(match, 'organisations', ':organisationID')}
 */
export const joinRouteMatchPath = (basePath: string, ...paths: string[]): string => {
  return join(basePath, ...paths);
};

export const relativePath = (...paths: string[]): string => {
  const path = join(...paths);
  return path.startsWith('/') ? path.slice(1) : path;
};

/**
 *
 * @param path
 * @param next
 * @returns
 */
export const resolveToParent = (path: string, next: string) => {
  const segments = path.split('/').filter(Boolean);
  segments.pop(); // remove current leaf
  // eslint-disable-next-line prefer-template
  return '/' + [...segments, next].join('/');
};

/**
 * Decode and parse base64 stringified json.
 * @function extractJsonFromBase64
 * @param {string} base64 Base64 encoded JSON string
 * @param {JSONType} defaultJson Default to return if parsing fails
 * @return {JSONType} Parsed JSON or default value
 */
export const extractJsonFromBase64 = (base64: string, defaultJson: JSONType = {}): JSONType => {
  try {
    return JSON.parse(window.atob(base64));
  } catch (error) {
    Logger.error(String(error));

    return defaultJson;
  }
};

/**
 * Interpolates given given keys into given path.
 *
 * @example:
 * const interpolated: string = interpolateValues('/workspaces/:workspaceId/organisations/:organisationId', {
 *   workspaceId: '6b2d6ba3-bb50-44e6-8781-5a1bce599ea9',
 *   organisationId: 'ede79e7b-f810-4e87-9f31-988e3eb0dfec'
 * });
 * > /workspaces/6b2d6ba3-bb50-44e6-8781-5a1bce599ea9/organisations/ede79e7b-f810-4e87-9f31-988e3eb0dfec
 *
 * @param {string} path
 * @param {Record<string, string>} keys
 * @returns {string}
 */
export const interpolatePathParams = (path: string, keys: Record<string, string>): string => {
  return Object.keys(keys).reduce((memo: string, key: string) => {
    const regex = new RegExp(`:${key}`);
    return memo.replace(regex, keys[key]);
  }, path);
};

/**
 * Get a param value from a route match object.
 * @param params - params object from useParams
 * @param paramKey - key of the param to retrieve
 * @returns param value, or undefined if not found
 */
export const getParamFromRoute = (params: Record<string, string | undefined>, paramKey: string): string | undefined =>
  params[paramKey];

/**
 * Takes a path and a route template path and extracts the params from it.
 *
 * @example
 *  getParamFromRoute('/workspace/abc-1234/', '/workspace/:workspaceId') = { workspaceId: 'abc-1234' }
 *
 * @param {string} path
 * @param {string} route
 * @returns {Params}
 */
export const getParamsByPath = (path: string, route: string): Params => {
  const tokenRegex = /:[A-Za-z0-9-]+/g;
  const matches = route.match(tokenRegex);

  if (matches) {
    return matches.reduce((memo: Params, token: string) => {
      const index = route.split('/').indexOf(token);
      memo[token.substr(1, token.length)] = path.split('/')[index] || null;
      return memo;
    }, {});
  }

  return {};
};

/**
 * Gets the value from the query-params passed as key
 * @param {Location} location
 * @param {string} queryParamKey
 * @returns value
 */
export const getValueFromQuery = (location: Location, queryParamKey: string): string | undefined => {
  const queryParams = queryString.parse(location.search);

  const result = queryParams[queryParamKey];

  if (!result) {
    return;
  }

  const value = Array.isArray(result) ? result[0] : result;

  return value ?? undefined;
};

/**
 * Returns the route match object if the given path (url) matches the given route
 * @param pathname - The URL to match against the route path
 * @param routePath - The route to match the URL against
 * @param isExact - Enforce that only exact matches are allowed. By default, matches with extra URL sections on the end will also be allowed.
 */
export const getPathMatch = (pathname: string, routePath: string, isExact = false): PathMatch<string> | null => {
  const routeMatch = matchPath(
    {
      path: routePath,
      end: isExact
    },
    pathname
  );

  return routeMatch;
};

export const useRoutePattern = (): string | null => {
  const matches = useMatches(); // returns array of all matched routes
  if (matches.length === 0) {
    return null;
  }

  const deepest = matches[matches.length - 1];
  return ((deepest as unknown) as { route: { path: string } }).route?.path ?? null; // the pattern path
};
